import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, AfterViewInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, UntypedFormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { interval, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DocumentLanguage, Template } from 'src/app/_shared/models/article';
import { BrandVoice, GetBrandVoiceListRequest, GetBrandVoiceListResponse, GetTargetAudienceListRequest, GetTargetAudienceListResponse, TargetAudience } from 'src/app/_shared/models/article';
import { ContentStudioTrackingService } from 'src/app/_shared/services/tracking/contentstudio-tracking.service';
import { MwcModalRef, MwcModalService } from 'mwc/modal';
import { MwcUploadChangeParam, MwcUploadFile } from 'mwc/upload';
import { MwcNotificationModule, MwcNotificationService } from 'mwc/notification';
import { AnalyzeUrlsRequest, AnalyzeUrlsResponse, UploadInstDocsRequest } from 'src/app/_shared/models/campaigns';
import { StorageHelper } from 'src/app/_utilities/storage-helper';
import { CampaignService } from 'src/app/services/campaign/campaign.service';
import { Router } from '@angular/router';
import { WebpageTemplateService } from 'src/app/services/webpageTemplate/webpageTemplate.service';
import { ArticleService } from '../../../../../services/article/article.service';
import { GenerateCampaignArticlesRequest } from 'src/app/_shared/models/GenerateCampaignArticlesRequest';
import { GenerationInProgressHelperService } from 'src/app/_shared/services/helper/generation-in-progress-popup/generation-in-progress-popup-helper.service';
import { CampaignData } from 'src/app/campaigns/view-campaigns/view-campaigns.component';
import { ArticleStoreService } from 'src/app/services/article/article-store.service';
import { WebpageTemplateRequest } from 'src/app/_shared/models/webpage-template.model';

export interface TemplateSection {
  id: string;
  sectionTitle: string;
  wordCount: number;
  contentPrompt: string;
  sortOrder:number;
}

export interface CustomTemplate {
  id: string;
  name: string;
  sections: TemplateSection[];
  isSelected?: boolean;
}

@Component({
  selector: 'app-custom-template-workflow',
  templateUrl: './custom-template-workflow.component.html',
  styleUrls: ['./custom-template-workflow.component.scss']
})
export class CustomTemplateWorkflowComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() template: Template;
  @ViewChild('keywordMenu') keywordMenu: any;
  @ViewChild('plusBtn') plusButton: ElementRef;
  _storageHelper: StorageHelper;
  workflowForm: FormGroup;
  selectedIndex = 0;
  private destroy$ = new Subject<void>();
  isLoading = false;
  isAnalyzing = false;

  
  isParentUrlVisible = true;
  isIndividualUrlVisible = false;
  
  // Define the menu items
  tabs = [
    { name: 'Analyze Website', iconName: 'file-search-03', index: 0, completed: false, disable:false },
    { name: 'Add Keywords', iconName: 'key-02', index: 1, completed: false, disable:true },
    { name: 'Page Configuration', iconName: 'settings-04', index: 2, completed: false, disable:true },
    { name: 'Brand Voice', iconName: 'clipboard-report', index: 3, completed: false, disable:true },
    { name: 'Target Audience', iconName: 'users-03', index: 4, completed: false, disable:true },
    { name: 'Language Check', iconName: 'translate-02', index: 5, completed: false, disable:true }
  ];

  seoAnalysisList: { title: string; desc?: string }[] = [
    { 'title': 'Focus on Quality Content', 'desc': 'Ensure your content is informative, engaging, and valuable to your audience' },
    { 'title': 'Optimize for Keywords', 'desc': 'Research and use relevant keywords naturally in your content to improve visibility' },
    { 'title': 'Use Descriptive URLs', 'desc': 'Make URLs descriptive, short, and keyword-rich to enhance understanding and SEO' },
    { 'title': 'Optimize Title Tags', 'desc': 'Include target keywords in title tags while making them compelling to click' },
    { 'title': 'Craft Compelling Meta Descriptions', 'desc': 'Write unique and enticing meta descriptions for each page to improve click-through rates.' },
    { 'title': 'Utilize Header Tags', 'desc': 'Structure your content with H1, H2, and H3 tags to improve readability and SEO.' },
    { 'title': 'Optimize Images', 'desc': 'Use descriptive filenames and alt text for images to improve accessibility and relevance.' },
    { 'title': 'Improve Site Speed', 'desc': 'Ensure your website loads quickly on all devices to reduce bounce rates and enhance user experience.' },
    { 'title': 'Mobile-Friendly Design', 'desc': 'Optimize your website for mobile users to cater to the increasing mobile traffic.' },
    { 'title': 'Secure Your Site with HTTPS', 'desc': 'Implement HTTPS to secure your site and boost its ranking signal.' },
    { 'title': 'Create Quality Backlinks', 'desc': 'Gain backlinks from reputable sites to enhance your site’s credibility and search ranking.' },
    { 'title': 'Use Social Media to Boost Visibility', 'desc': 'Share your content on social media platforms to increase traffic and engagement.' },
    { 'title': 'Implement Local SEO Practices', 'desc': 'Optimize your site for local search terms and listings to attract local customers.' },
    { 'title': 'Monitor Your Sites Performance', 'desc': 'Use tools like Google Analytics and Search Console to track traffic, performance, and issues.' },
    { 'title': 'Update Content Regularly', 'desc': 'Keep your site fresh with regular updates and new content to engage visitors and search engines.' },
    { 'title': 'Optimize for Voice Search', 'desc': 'Include conversational keywords and FAQs to capture voice search queries.' },
    { 'title': 'Use Internal Linking Wisely', 'desc': 'Create internal links to guide visitors to relevant content, improving site navigation and SEO.' },
    { 'title': 'Avoid Duplicate Content', 'desc': 'Ensure your content is unique across your site to avoid penalties from search engines.' },
    { 'title': 'Leverage Schema Markup', 'desc': 'Implement structured data to help search engines understand and display your content more prominently.' },
    { 'title': 'Stay Updated with SEO Practices', 'desc': 'SEO is ever-evolving; stay informed about the latest trends and algorithm updates to keep your site competitive.' }
  ];
  
  // Content Instructions tab (will be shown when Upload Instructions is clicked)
  showContentInstructions = false;
  contentInstructionsTab = { name: 'Content Instructions', iconName: 'file-question-02', index: 6, completed: false };
  
  // Table selection properties
  checked = false;
  indeterminate = false;
  setOfCheckedId = new Set<number>();
  
  // Brand Voice properties
  isNoCreateBrandVoice = false;
  //filteredBrandVoiceList = [
  //  {
  //    label: 'Professional Voice', 
  //    value: '1', 
  //    tone: 'Professional', 
  //    createdOn: '2025-08-20', 
  //    createdBy: 'John Smith', 
  //    brandVoiceDocument: "Our brand voice is professional, authoritative, and knowledgeable. We use clear, concise language that communicates expertise while remaining accessible. We avoid jargon except when necessary and always explain complex concepts in simple terms. Our tone is confident but not arrogant, helpful but not condescending.",
  //    details: null,
  //    isDefault: true,
  //    isSelectedBV: false
  //  },
  //  {
  //    label: 'Friendly Voice', 
  //    value: '2', 
  //    tone: 'Friendly', 
  //    createdOn: '2025-08-15', 
  //    createdBy: 'Jane Doe', 
  //    brandVoiceDocument: "Our brand voice is friendly, approachable, and conversational. We speak to our customers as equals, using simple, everyday language. We're warm and personable, using contractions and occasionally informal expressions. Our writing feels like a helpful friend giving advice.",
  //    details: null,
  //    isDefault: false,
  //    isSelectedBV: false
  //  },
  //  {
  //    label: 'Luxury Voice', 
  //    value: '3', 
  //    tone: 'Luxury', 
  //    createdOn: '2025-08-10', 
  //    createdBy: 'Michael Johnson', 
  //    brandVoiceDocument: "Our brand voice embodies refined luxury and sophistication. We use elegant, carefully chosen language that evokes quality and exclusivity. Our tone is polished and cultivated without being pretentious. We focus on the exceptional experience and meticulous attention to detail that defines our brand.",
  //    details: null,
  //    isDefault: false,
  //    isSelectedBV: false
  //  }
  //];
  brandVoiceDetails: any;
  bvSearchField: FormControl = new FormControl();
  isShowDetailsBV = false;
  FoundSerchBV = true;
  searchTxt = '';
  timeout = null;
  brandVoiceList = [];
  filteredBrandVoiceList = [];
  selectedBrandVoice: string;
  
  // Target Audience properties
  //isNoTargetAudience = false;
  //filteredTargetAudienceList = [
  //  {
  //    targetAudienceId: 1,
  //    title: 'Business Professionals',
  //    authorName: 'John Smith',
  //    thingTargetAudience: {
  //      thingJson: {
  //        title: 'Business Professionals',
  //        gender: ['Male', 'Female'],
  //        ageGroup: ['Older Adults (35 to 50)', 'Young Adults (25-34)'],
  //        relationshipStatus: ['Married', 'Single'],
  //        industry: ['Technology', 'Finance', 'Consulting'],
  //        interests: ['Professional Development', 'Technology', 'Business News'],
  //        intent: ['Research', 'Professional Growth']
  //      }
  //    },
  //    createdDate: '2025-08-20',
  //    isDefault: true,
  //    isSelectedTA: false
  //  },
  //  {
  //    targetAudienceId: 2,
  //    title: 'Frequent Travelers',
  //    authorName: 'Jane Doe',
  //    thingTargetAudience: {
  //      thingJson: {
  //        title: 'Frequent Travelers',
  //        gender: ['Male', 'Female', 'Non-Binary'],
  //        ageGroup: ['Young Adults (25-34)', 'Older Adults (35 to 50)'],
  //        relationshipStatus: ['Single', 'Married', 'Divorced'],
  //        industry: ['Various'],
  //        interests: ['Travel', 'Culture', 'Food & Wine', 'Photography'],
  //        intent: ['Trip Planning', 'Inspiration', 'Deals']
  //      }
  //    },
  //    createdDate: '2025-08-15',
  //    isDefault: false,
  //    isSelectedTA: false
  //  },
  //  {
  //    targetAudienceId: 3,
  //    title: 'Young Families',
  //    authorName: 'Michael Johnson',
  //    thingTargetAudience: {
  //      thingJson: {
  //        title: 'Young Families',
  //        gender: ['Male', 'Female'],
  //        ageGroup: ['Young Adults (25-34)'],
  //        relationshipStatus: ['Married'],
  //        industry: ['Various'],
  //        interests: ['Parenting', 'Education', 'Family Activities', 'Health'],
  //        intent: ['Information', 'Solutions', 'Community']
  //      }
  //    },
  //    createdDate: '2025-08-10',
  //    isDefault: false,
  //    isSelectedTA: false
  //  }
  //];
  //targetAudienceDetails: any;
  tvSearchField: FormControl = new FormControl();
  isShowDetailsTA = false;
  FoundSearchTA = true;
  isNoTargetAudience: boolean = true;
  selectedTargetAudience: string;
  targetAudienceList = [];
  filteredTargetAudienceList = [];
  targetAudienceDetails: TargetAudience;
  
  // Language properties
  selectedValueLanguage = 'US';
  provinceData = [
    { id: 12, label: "English", languageCode: "en", value: "US" }, // United States
    { id: 199, label: "English(UK)", languageCode: "gb", value: "GB" }, // English(UK)
    { id: 16, label: "French", languageCode: "fr", value: "FR" }, // France
    { id: 15, label: "German", languageCode: "de", value: "DE" }, // Germany
    { id: 11, label: "Hindi", languageCode: "hi", value: "IN" }, // India
    { id: 86, label: "Indonesian", languageCode: "id", value: "ID" }, // Indonesia
    { id: 29, label: "Japanese", languageCode: "ja", value: "JP" }, // Japan
    { id: 18, label: "Korean", languageCode: "ko", value: "KR" }, // South Korea
    { id: 190, label: "Mandarin", languageCode: "zh-CN", value: "CN" }, // China
    { id: 9, label: "Portuguese", languageCode: "pt", value: "PT" }, // Portugal
    { id: 8, label: "Russian", languageCode: "ru", value: "RU" }, // Russia
    { id: 17, label: "Spanish", languageCode: "es", value: "ES" }, // Spain
    { id: 32, label: "Spanish (Mexico)", languageCode: "es-mx", value: "MX" }, // Mexico
    { id: 31, label: "Swahili", languageCode: "sw", value: "KE" }, // Kenya
    { id: 170, label: "Turkish", languageCode: "tr", value: "TR" }, // Turkey
    { id: 25, label: "Vietnamese", languageCode: "vi", value: "VN" }, // Vietnam
  ];
  
  // Dummy data for keyword options
  listOfOption = [];
  listOfTagOptions: string[] = [];
  
  // For keyword dropdown
  activePageIndex: number | null = null;
  selectedKeywords: string[] = [];
  keywordInputVisible = false;
  selectedKeywordInputValue = '';
  maxKeywordsAllowed = 6;
  isKeywordDiscoveryEnabled = true;
  profileId: number;
  campaignId: string;
  @ViewChild('inputElement', { static: false }) inputElement?: ElementRef;
  
  // Dummy data for templates
  templateOptions = [];

  // Uploaded files data
  uploadedFiles = [];

  // Document type options
  documentTypeOptions = [
    { label: 'Brand Guidelines', value: 'brand-guidelines' },
    { label: 'Content Strategy', value: 'content-strategy' },
    { label: 'Style Guide', value: 'style-guide' },
    { label: 'Product Information', value: 'product-info' },
    { label: 'Marketing Material', value: 'marketing-material' },
    { label: 'Technical Documentation', value: 'technical-doc' },
    { label: 'Other', value: 'other' }
  ];

  maxDocuments = 3;
  isDefaultBVAvailable: boolean = false;
  defaultBVId: number = 0;
  isDefaultTAAvailable: boolean = false;
  defaultTAId: number = 0;
  
  // Format function for the progress percentage display
  percentFormat = (percent: number) => `${percent}%`;
  
  // Dummy data for pages with assigned keywords
  pages = [];
  templates: CustomTemplate[] = [];
  // Properties for generation modal
  showGenerationModal = false;

  constructor(
    private fb: FormBuilder,
    private tracking: ContentStudioTrackingService,
    private modalRef: MwcModalRef,
    private cdr: ChangeDetectorRef,
    private notification: MwcNotificationService,
    public cdRef: ChangeDetectorRef,
    private campaignService: CampaignService,
    private router: Router,
    private modalService: MwcModalService,
    private webpageTemplateService: WebpageTemplateService,
    private articleService: ArticleService,
    private generationInProgressHelperService: GenerationInProgressHelperService,
    private articleStoreService: ArticleStoreService,
  ) {
    this._storageHelper = new StorageHelper();
  }

  campaignName:string = '';
  ngOnInit(): void {
    this.initForm();
    const mwcData = this.modalRef.getConfig().mwcData || {};
    if (mwcData.isIndividualUrls != null) {
      this.isIndividualUrlVisible = mwcData.isIndividualUrls;
      this.isParentUrlVisible = !mwcData.isIndividualUrls;
    }
    if (mwcData.urls != null && Array.isArray(mwcData.urls) && mwcData.urls.length > 0) {
      if (mwcData.isIndividualUrls) {
        this.workflowForm.get('individualUrls')?.setValue(mwcData.urls.join('\n'));
        this.isIndividualUrlVisible = true;
        this.isParentUrlVisible = false;
      } else {
        this.workflowForm.get('websiteUrl')?.setValue(mwcData.urls[0]);
        this.isIndividualUrlVisible = false;
        this.isParentUrlVisible = true;
      }
    }
    if (mwcData.keywords != null && Array.isArray(mwcData.keywords) && mwcData.keywords.length > 0) {
      this.selectedKeywords = [...mwcData.keywords];
      this.workflowForm.get('keywords')?.setValue(mwcData.keywords.join(', '));
      this.listOfOption = Array.from(new Set([...this.listOfOption, ...mwcData.keywords.map(k => ({ label: k, value: k }))]));
    }
    if (mwcData.selectedTabIndex != null && !isNaN(mwcData.selectedTabIndex)) {
      this.selectedIndex = mwcData.selectedTabIndex;
    }
    if(mwcData.templates != null && Array.isArray(mwcData.templates) && mwcData.templates.length > 0){
      this.templates = mwcData.templates.map((t, index) => ({
              id: t._id,
              name: t.name,
              sections: t.sections?.map(s => ({
                id: s.id ?? '',
                sectionTitle: s.title,
                wordCount: s.wordCount,
                contentPrompt: s.contentPrompt,
                sortOrder: s.sortOrder
              })) || [],
              isSelected: index === 0
            }));
      this.templateOptions = this.templates.map(t => ({
            label: t.name,   
            value: t.id   
          }));      
    }
    if(mwcData.webpageConfigurationAndDocumentDetails != null){
      const detailsArray = mwcData.webpageConfigurationAndDocumentDetails;
      const details = detailsArray[0];
      this.pages = details.pages.map((page, index) => ({
        id: index + 1,
        title: page.title,
        url: page.pageUrl,
        keywords: page.keywords || [],
        template: page.templateId,
        disabled: false
      }));
        this.profileId = details.profileId;
        this.campaignId = details.campaignId;
    }
    if(mwcData.campaignName){
      this.campaignName = mwcData.campaignName;
    }
    // Initialize brand voice and target audience search fields
    //this.bvSearchField.valueChanges.pipe(
    //  takeUntil(this.destroy$)
    //).subscribe(val => {
    //  this.triggerSearchBV(val);
    //});

    // Initialize Target Audience search field
    //this.tvSearchField.valueChanges.pipe(
    //  takeUntil(this.destroy$)
    //).subscribe(val => {
    //  this.triggerSearchTA(val);
    //});
    
    // Ensure no brand voice or target audience is selected by default
    this.filteredBrandVoiceList.forEach(bv => {
      bv.isSelectedBV = false;
    });
    this.filteredTargetAudienceList.forEach(ta => {
      ta.isSelectedTA = false;
    });

    this.fetchBrandVoices();
    this.fetchTargetAudiences();
    // Initialize language dropdown with default value
    this.selectedValueLanguage = 'US';
    this.languageChanged(this.selectedValueLanguage);
    
    // Track page view
    // this.tracking.trackEvent(
    //   'pageview', `Custom Template Workflow - ${this.template?.title || 'Unknown'}`
    // );
    this.getTemplates();
  }
  
  getTemplates(){
    var req:WebpageTemplateRequest = {
      profileId: this._storageHelper.user.pId,
      brandId: null
    }
    this.webpageTemplateService.getTemplates(req).subscribe({
      next: (res) => {
        if (res.success && res.templates) {
          this.templates = res.templates.map((t, index) => ({
            id: t._id,
            name: t.name,
            sections: t.sections?.map(s => ({
              id: s.id ?? '',
              sectionTitle: s.title,
              wordCount: s.wordCount,
              contentPrompt: s.contentPrompt,
              sortOrder: s.sortOrder
            })) || [],
            isSelected: index === 0
          }));
          this.templateOptions = res.templates.map(t => ({
            label: t.name,   
            value: t._id   
          }));
        } else {
          this.templateOptions = [];
          this.templates = [];
        }
      },
      error: () => {
        this.templateOptions = [];
        this.templates = [];
      }
    });
  }

  private initForm(): void {
    this.workflowForm = this.fb.group({
      websiteUrl: [''],
      keywords: [''],
      pageTitle: [this.template?.titleText || ''],
      pageDescription: [''],
      brandVoice: this.selectedBrandVoice,
      brandVoiceDocument: "",
      brandVoiceDetails: null,
      brandVoiceId: 0,
      targetAudience: this.selectedTargetAudience,
      targetAudienceId: 0,
      targetAudienceDetails: null,
      language: ['English (US)'],
      individualUrls: new UntypedFormControl('',[this.multiUrlValidator]),
    });
  }

  // Reference to pageData for backward compatibility
  get pageData() {
    return this.pages;
  }
  
  // Method to handle adding a new keyword to a page
  addKeywordToPage(pageIndex: number): void {
    this.activePageIndex = pageIndex;
    // Initialize selected keywords with the ones already on the page
    this.selectedKeywords = [...this.pages[pageIndex].keywords];
  }
  
  // Method to cancel keyword selection and close dropdown
  cancelKeywordSelection(): void {
    // Close dropdown using DOM API
    this.closeDropdown();
    this.selectedKeywords = [];
    this.activePageIndex = null;
  }
  
  // Method to add selected keywords to the page and close dropdown
  confirmKeywordSelection(): void {
    if (this.activePageIndex !== null && this.selectedKeywords.length > 0) {
      // Replace the page's keywords with the selected ones
      this.pages[this.activePageIndex].keywords = [...this.selectedKeywords];
      // Track the action
      this.tracking.trackEvent(
        'action', `Added ${this.selectedKeywords.length} keywords to page ${this.pages[this.activePageIndex].title}`
      );
    }
    
    // Close dropdown using DOM API
    this.closeDropdown();
    this.selectedKeywords = [];
    this.activePageIndex = null;
  }
  
  // Helper method to close dropdown
  private closeDropdown(): void {
    // Use DOM API to click outside the dropdown
    setTimeout(() => {
      document.body.click();
    }, 0);
  }
  
  // Keyword management methods
  removeKeyword(keyword: string): void {
    this.selectedKeywords = this.selectedKeywords.filter(selectedKeyword => selectedKeyword !== keyword);
    this.tracking.trackEvent('action', `Removed keyword: ${keyword}`);
  }
  
  showKeywordInput(): void {
    this.keywordInputVisible = true;
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.nativeElement.focus();
      }
    }, 10);
  }
  
  addKeywordToSelectedKeywords(): void {
    if (this.selectedKeywordInputValue && this.selectedKeywords.indexOf(this.selectedKeywordInputValue) === -1 && this.selectedKeywords.length < this.maxKeywordsAllowed) {
      this.selectedKeywords = [...this.selectedKeywords, this.selectedKeywordInputValue];
      this.tracking.trackEvent('action', `Added keyword: ${this.selectedKeywordInputValue}`);
    }
    this.selectedKeywordInputValue = '';
    this.keywordInputVisible = false;
  }
  
  sliceKeyword(keyword: string): string {
    return keyword.length > 15 ? keyword.slice(0, 15) + '...' : keyword;
  }
  
  discoverKeywords(): void {
    // Implementation for discovering keywords would go here
    console.log('Discover Keywords clicked');
    this.tracking.trackEvent('action', 'Discover Keywords clicked');
  }
  
  // Toggle keyword selection
  toggleKeywordSelection(keyword: string): void {
    const index = this.selectedKeywords.indexOf(keyword);
    if (index >= 0) {
      this.selectedKeywords.splice(index, 1);
    } else {
      this.selectedKeywords.push(keyword);
    }
  }

  // Method to handle "Proceed" from the Add Keywords tab
  // proceedFromKeywords(): void {
  //   this.isAnalyzing = true;
    
  //   // Save the selected keywords to the form
  //   this.workflowForm.get('keywords').setValue(this.selectedKeywords.join(', '));
    
  //   // Simulate analysis for 3 seconds
  //   setTimeout(() => {
  //     this.isAnalyzing = false;
  //     this.selectedIndex = 2;
      
  //     // Mark the previous tab as completed
  //     this.tabs[1].completed = true;
      
  //     // Track progress
  //     this.tracking.trackEvent(
  //       'action', `Workflow - Proceed to ${this.tabs[2].name}`
  //     );
  //   }, 3000);
  // }

  proceedFromUploadInstructions(): void {
    this.isAnalyzing = true;   
    // Simulate analysis for 3 seconds
    setTimeout(() => {
      this.isAnalyzing = false;
      this.selectedIndex = 3;

      // Mark the previous tab as completed
      this.tabs[1].completed = true;

      // Track progress
      this.tracking.trackEvent(
        'action', `Workflow - Proceed to ${this.tabs[3].name}`
      );
    }, 3000);

    this.UploadInstructionsDocs();
  }



  proceedFromKeywords(): void {
    this.isAnalyzing = true;
    this.workflowForm.get('keywords').setValue(this.selectedKeywords.join(', '));
    const keywords = this.selectedKeywords.map(k => k.trim()).filter(k => k);
    let urls: string[] = [];
    const individualUrlsRaw = this.workflowForm.get('individualUrls')?.value;
    const websiteUrl = this.workflowForm.get('websiteUrl')?.value;
    if (individualUrlsRaw && individualUrlsRaw.trim()) {
      urls = individualUrlsRaw
        .split(/[\n,]/)
        .map(u => u.trim())
        .filter(u => u);
    } else if (websiteUrl && websiteUrl.trim()) {
      urls = [websiteUrl.trim()];
    }
    const request: AnalyzeUrlsRequest = {
      urls: urls,
      keywords: keywords,
      profileId: this._storageHelper.user.pId,
      businessId: Number(this._storageHelper.user.bId),
      profileName: this._storageHelper.user.profileName,
      isIndividualUrls: !!individualUrlsRaw && individualUrlsRaw.trim().length > 0,
      brandId : null,
    };
    this.campaignService.AnalyzeUrls<AnalyzeUrlsResponse>(request).subscribe({
      next: (response: AnalyzeUrlsResponse) => {
        this.isAnalyzing = false;
        if (response && response.success) {
          this.tabs[1].completed = true;
          this.selectedIndex = 2;
          this.tracking.trackEvent(
            'action',
            `Workflow - Proceed to ${this.tabs[2].name}`
          );
          this.modalService.closeAll();
          this.router.navigate(['/campaigns/my-campaigns']);
        } else {
          console.error('Analysis failed:', response?.message);
        }
      },
      error: (err) => {
        this.isAnalyzing = false;
        console.error('Error calling AnalyzeUrls API', err);
      }
    });
  }
  
  // Method to check if a keyword is selected
  isKeywordSelected(keyword: string): boolean {
    return this.selectedKeywords.includes(keyword);
  }

  // Handle select all checkbox
  onAllChecked(checked: boolean): void {
    this.pages.forEach(page => {
      if (checked) {
        this.setOfCheckedId.add(page.id);
      } else {
        this.setOfCheckedId.delete(page.id);
      }
    });
    this.refreshCheckedStatus();
  }

  // Handle individual item checked
  onItemChecked(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
    this.refreshCheckedStatus();
  }

  // Refresh the checked status
  refreshCheckedStatus(): void {
    const listOfEnabledData = this.pages.filter(page => !page.disabled);
    this.checked = listOfEnabledData.every(page => this.setOfCheckedId.has(page.id));
    this.indeterminate = listOfEnabledData.some(page => this.setOfCheckedId.has(page.id)) && !this.checked;
  }

  // Upload instructions method
  uploadInstructions(): void {
    // Show the Content Instructions tab
    this.showContentInstructions = true;
    
    // Set the selected index to the Content Instructions tab
    this.selectedIndex = this.contentInstructionsTab.index;
    
    // Track the action
    this.tracking.trackEvent(
      'action', 'Content Instructions tab added'
    );
    
    // Force change detection to update the UI
    this.cdr.detectChanges();
  }

  tabSelectChange(event: any): void {
    const [type, tab] = event;
    if (type === 'click') {
      // Store the previous tab index for special handling
      const previousTab = this.selectedIndex;
      
      // Update to the new tab
      this.selectedIndex = tab.index;
      
      // Prepare tab-specific logic
      if (this.selectedIndex === 3) { // Brand Voice tab
        // Check if we have selected brand voice already
        const brandVoice = this.workflowForm.get('brandVoice').value;
        if (brandVoice) {
          // We already have a selected brand voice, make sure flag is updated
          this.isNoCreateBrandVoice = false;
        }
      } 
      else if (this.selectedIndex === 4) { // Target Audience tab
        // Check if we have selected target audience already
        const targetAudience = this.workflowForm.get('targetAudience').value;
        if (targetAudience) {
          // We already have a selected target audience, make sure flag is updated
          this.isNoTargetAudience = false;
        }
      }
    }
  }

  tabsSetSelectChange(event: any): void {
    const [tabInfo] = event;
    this.selectedIndex = tabInfo.index;
    
    // Mark previous tabs as completed
    this.tabs.forEach(tab => {
      tab.completed = tab.index < this.selectedIndex;
    });
  }

  getRandomItems = (list: any[], count: number): any[] => {
    const shuffled = list.sort(() => 0.5 - Math.random()); // Shuffle the list
    return shuffled.slice(0, count); // Return the first `count` items
  };

  formatDate(isoString) {
    const date = new Date(isoString);
    const day = date.getUTCDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getUTCFullYear();

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th'; // covers 11-20
      switch (n % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  }

  onSubmit(): void {
    if (!this.workflowForm.valid) {
      return;
    }
    this.isLoading = true;
    const campaignRequest = new GenerateCampaignArticlesRequest();
    campaignRequest.CampaignTopic = this.campaignName || "";
    campaignRequest.CampaignGoal = this.workflowForm.get('campaignGoal')?.value || "";
    campaignRequest.contentBrief = this.workflowForm.get('contentBrief')?.value || "";
    campaignRequest.tone = this.workflowForm.get('tone')?.value || "";
    campaignRequest.Templates = []; // add templates if you have them
    campaignRequest.targetAudienceId = this.workflowForm.get('targetAudienceId')?.value;
    campaignRequest.brandVoiceId = this.workflowForm.get('brandVoiceId')?.value;
    const selectedLanguage = this.provinceData.find(x => x.value === this.selectedValueLanguage);
    if (selectedLanguage) {
      const languageDetail = new DocumentLanguage();
      languageDetail.id = selectedLanguage.id;
      languageDetail.countryCode = selectedLanguage.value;
      languageDetail.languageName = selectedLanguage.label;
      languageDetail.languageCode = selectedLanguage.languageCode;
      campaignRequest.languageDetail = languageDetail;
      campaignRequest.language = selectedLanguage.label;
    }
    this.generationInProgressHelperService.openWaitingPopup([], 'Generation in progress...', false, false, true);
    this.articleService.GenerateCampaignArticles(campaignRequest).subscribe({
      next: (res) => {
        const campaignData = new CampaignData();
        campaignData.campaignId = res.campaignId;
        campaignData.CampaignTopic = campaignRequest.CampaignTopic;
        campaignData.createddate = this.formatDate(new Date());
        this.articleStoreService.updateCampaignState(campaignData);
        const navigationExtras: any = {
          queryParams: {}
        };
        if (res.campaignId) {
          navigationExtras.queryParams.campaignId = res.campaignId;
        }
        setTimeout(() => {
          this.router.navigate(['/campaigns/view-campaigns'], navigationExtras);
        }, 100);
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error("GenerateCampaignArticles error:", err);
        this.generationInProgressHelperService.closeTemplateSelector();
        this.isLoading = false;
      },
      complete: () => {
        setTimeout(() => {
          this.generationInProgressHelperService.closeTemplateSelector();
          this.isLoading = false;
        }, 5000);
      }
    });
    this.modalRef.close(null);
  }

  closeGenerationModal(): void {
    this.showGenerationModal = false;
    this.generationInProgressHelperService.closeTemplateSelector();
  }

  onCancel(): void {
    this.modalRef.close(null);
  }
  
  onChangeWorkflow(): void {
    // Logic for changing workflow
    // For example, you might want to go back to template selection
    this.modalRef.close({ action: 'changeWorkflow' });
  }
  
  onNext(): void {
    // Validate current tab if needed
    if (this.validateCurrentTab()) {
      // For Analyze Website tab or Brand Voice tab, show analyzing state
      if (this.selectedIndex === 3) {
        this.isAnalyzing = true;
        
        // Set appropriate analyzing message based on the tab
        const analyzingMessage =  'Analyzing the documents';
        
        // Simulate analysis for 3 seconds
        setTimeout(() => {
          this.isAnalyzing = false;
          
          // Move to the next tab
          if (this.selectedIndex < this.tabs.length - 1) {
            this.selectedIndex++;
            
            // Mark the previous tab as completed
            this.tabs[this.selectedIndex - 1].completed = true;
            
            // Track progress
            this.tracking.trackEvent(
              'action', `Workflow - Next to ${this.tabs[this.selectedIndex].name}`
            );
          }
        }, 3000);
      }      
      else {
        // For other tabs, proceed immediately
        // Move to the next tab
        if (this.selectedIndex < this.tabs.length - 1) {
          this.selectedIndex++;
          
          // Mark the previous tab as completed
          this.tabs[this.selectedIndex - 1].completed = true;
          
          // Track progress
          this.tracking.trackEvent(
            'action', `Workflow - Next to ${this.tabs[this.selectedIndex].name}`
          );
        }
      }
    }
  }
  
  private validateCurrentTab(): boolean {
    // Add validation logic for each tab if needed
    switch(this.selectedIndex) {
      case 0: // Analyze Website
        const websiteUrl = this.workflowForm.get('websiteUrl').value;
        if (!websiteUrl) {
          // Handle validation error - for now just return true to allow navigation
          return true;
        }
        return true;
        
      case 1: // Add Keywords
        // Add validation for keywords if needed
        return true;
        
      case 2: // Page Configuration
        const pageTitle = this.workflowForm.get('pageTitle').value;
        if (!pageTitle) {
          // Handle validation error
          return true;
        }
        return true;
        
      case 3: // Brand Voice
        // For Brand Voice tab, validation should pass if either:
        // 1. isNoCreateBrandVoice is true (user chose to skip brand voice)
        // 2. A brand voice has been selected (value is set in the form)
        if (this.isNoCreateBrandVoice) {
          return true; // Skip validation if no brand voice is needed
        }
        
        const brandVoice = this.workflowForm.get('brandVoice').value;
        if (!brandVoice) {
          // Show an error message if needed
          console.warn('Please select a brand voice before proceeding');
          // Still allow navigation for now
          return true;
        }
        return true;
        
      case 4: // Target Audience
        // For Target Audience tab, validation should pass if either:
        // 1. isNoTargetAudience is true (user chose to skip target audience)
        // 2. A target audience has been selected (value is set in the form)
        if (this.isNoTargetAudience) {
          return true; // Skip validation if no target audience is needed
        }
        
        const targetAudience = this.workflowForm.get('targetAudience').value;
        if (!targetAudience) {
          // Show an error message if needed
          console.warn('Please select a target audience before proceeding');
          // Still allow navigation for now
          return true;
        }
        return true;
        
      case 5: // Language Check
        const language = this.workflowForm.get('language').value;
        if (!language) {
          // If no language is selected
          // You could show an error message here
          return false;
        }
        return true;
        
      default:
        return true;
    }
  }
  
  // Methods for Brand Voice
  createBrandVoice(): void {
    // Implementation for creating a new brand voice
    console.log('Create Brand Voice clicked');
    // This would typically open a modal or navigate to a form
    this.tracking.trackEvent(
      'action', 'Create Brand Voice clicked'
    );
  }

  SelectedBV(value: string): void {
    // Find the selected brand voice
    const selectedBV = this.filteredBrandVoiceList.find(bv => bv.value === value);
    if (selectedBV) {
      // Reset previously selected
      this.filteredBrandVoiceList.forEach(bv => {
        bv.isSelectedBV = false;
      });
      // Mark this one as selected
      selectedBV.isSelectedBV = true;
      
      // Update form control
      this.workflowForm.get('brandVoice').setValue(selectedBV.label);
      
      // Make sure isNoCreateBrandVoice is set to false since we've selected a brand voice
      this.isNoCreateBrandVoice = false;
      
      // Track selection
      this.tracking.trackEvent(
        'action', `Selected Brand Voice: ${selectedBV.label}`
      );
    }
  }

  selectBV(value) {
    this.selectedBrandVoice = value;
    this.brandVoiceChange(value);
    this.isShowDetailsBV = true;
    let data = this.filteredBrandVoiceList.filter(item => { if (item.value == value) return item })[0];
    if (data != null && data != undefined) {
      if (data.isDefault) {
        this.isDefaultBVAvailable = true;
      }
      else {
        this.isDefaultBVAvailable = false;
      }
    }

    // Update form control
    this.workflowForm.get('brandVoice').setValue(data.label);

    // Track selection
    this.tracking.trackEvent(
      'action', `Selected Brand Voice: ${data.label}`
    );
  }

  brandVoiceChange(event) {
    let data = this.brandVoiceList.filter(item => { if (item.value == event) return item })[0];
    if (data != null && data != undefined) {
      this.brandVoiceDetails = data;
      this.workflowForm.patchValue(
        {
          brandVoiceDocument: data.brandVoiceDocument,
          brandVoiceDetails: data.details,
          brandVoiceId: +data.value
        }
      );
    }
  }
  viewAllBV(): void {
    this.isShowDetailsBV = false;
    this.filteredBrandVoiceList.forEach(bv => {
      bv.isSelectedBV = false;
    });
  }

  changeBV(item: any = null): void {
    if (item) {
      this.brandVoiceDetails = item;
    } else {
      this.isShowDetailsBV = false;
      this.filteredBrandVoiceList.forEach(bv => {
        bv.isSelectedBV = false;
      });
    }
  }

  triggerSearchBV() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.searchBrandVoice(this.searchTxt);
    }, 1000);
  }

  searchBrandVoice(searchText: string = "") {
    const query = searchText.toLowerCase();
    this.filteredBrandVoiceList = this.brandVoiceList.filter(s => s.tone.toLowerCase().includes(query) || s.label.toLowerCase().includes(query) || s.createdBy.toLowerCase().includes(query));
    if (this.filteredBrandVoiceList.length == 0) {
      this.FoundSerchBV = false;
      this.filteredBrandVoiceList = this.brandVoiceList;
    }
    else {
      this.FoundSerchBV = true;
      this.filteredBrandVoiceList = [...this.filteredBrandVoiceList];
    }
    this.cdRef.detectChanges();
  }
  // Target Audience methods
  createTargetAudience(): void {
    // Implementation for creating a new target audience
    console.log('Create Target Audience clicked');
    // This would typically open a modal or navigate to a form
    this.tracking.trackEvent(
      'action', 'Create Target Audience clicked'
    );
  }

  SelectedTA(title: string): void {
    // Find the selected target audience
    const selectedTA = this.filteredTargetAudienceList.find(ta => ta.title === title);
    if (selectedTA) {
      // Reset previously selected
      this.filteredTargetAudienceList.forEach(ta => {
        ta.isSelectedTA = false;
      });
      // Mark this one as selected
      selectedTA.isSelectedTA = true;
      
      // Update form control
      this.workflowForm.get('targetAudience').setValue(selectedTA.title);
      
      // Make sure isNoTargetAudience is set to false since we've selected a target audience
      this.isNoTargetAudience = false;
      
      // Track selection
      this.tracking.trackEvent(
        'action', `Selected Target Audience: ${selectedTA.title}`
      );
    }
  }

  selectTA(value) {
    this.selectedTargetAudience = value;
    this.targetAudienceChange(this.selectedTargetAudience);
    this.isShowDetailsTA = true;
    let data = this.filteredTargetAudienceList.filter(item => { if (item.title == value) return item })[0];
    if (data != null && data != undefined) {
      if (data.isDefault) {
        this.isDefaultTAAvailable = true;
      }
      else {
        this.isDefaultTAAvailable = false;
      }
    }
  }

  targetAudienceChange(event) {
    this.tracking.trackEvent(
      'pageview', 'Target Audience Dropdown Selection'
    );
    let data = this.targetAudienceList.filter(item => { if (item.title == event) return item })[0];
    if (data != null && data != undefined) {
      this.targetAudienceDetails = data;
      this.workflowForm.patchValue(
        {
          targetAudienceDetails: data.thingTargetAudience.thingJson,
          targetAudienceId: +data.targetAudienceId
        }
      );
    }
  }

  selectTV(title: string): void {
    // Find the selected target audience
    const selectedTA = this.filteredTargetAudienceList.find(ta => ta.title === title);
    if (selectedTA) {
      this.selectTA(selectedTA);
    }
  }

  viewAllTA(): void {
    this.isShowDetailsTA = false;
    this.filteredTargetAudienceList.forEach(ta => {
      ta.isSelectedTA = false;
    });
  }

  changeTA(item: any = null): void {
    if (item) {
      this.targetAudienceDetails = item;
    } else {
      this.isShowDetailsTA = false;
      this.filteredTargetAudienceList.forEach(ta => {
        ta.isSelectedTA = false;
      });
    }
  }

  triggerSearchTA() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.searchTargetAudience(this.searchTxt);
    }, 1000);
  }

  searchTargetAudience(searchText: string = "") {
    const query = searchText.toLowerCase();
    this.filteredTargetAudienceList = this.targetAudienceList.filter(s => {
      const titleMatch = s.title.toLowerCase().includes(query);
      const authorMatch = s.authorName.toLowerCase().includes(query);
      const genderMatch = s.thingTargetAudience?.thingJson?.gender?.some((g: string) =>
        g.toLowerCase().includes(query)
      );
      const ageGroupMatch = s.thingTargetAudience?.thingJson?.ageGroup?.some((ag: string) =>
        ag.toLowerCase().includes(query)
      );
      return titleMatch || authorMatch || genderMatch || ageGroupMatch;
    });
    this.FoundSearchTA = this.filteredTargetAudienceList.length > 0;
    if (!this.FoundSearchTA) {
      this.filteredTargetAudienceList = this.targetAudienceList;
    }
    this.cdRef.detectChanges();
  }

  // Utility methods
  reloadList(event: string) {
    console.log(`Reload ${event} list clicked`);
    this.tracking.trackEvent(
      'action', `Reload ${event} list clicked`
    );
    if (event == "targetAudience") {
      this.fetchTargetAudiences(true);
    } else {
      this.fetchBrandVoices(true);
    }
  }

  //getDaysDifference(date: string): string {
  //  const createdDate = new Date(date);
  //  const today = new Date();
  //  const diffTime = Math.abs(today.getTime() - createdDate.getTime());
  //  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
  //  if (diffDays === 0) {
  //    return 'Today';
  //  } else if (diffDays === 1) {
  //    return 'Yesterday';
  //  } else {
  //    return `${diffDays} days ago`;
  //  }
  //}

  // Language methods
  languageChanged(event: any = null): void {
    console.log('languageChanged called with event:', event);
    
    // Get the selected language value (from event or current selection)
    const languageValue = event || this.selectedValueLanguage;
    
    // Get the selected language object
    const selectedLanguage = this.provinceData.find(lang => lang.value === languageValue);
    if (selectedLanguage) {
      console.log('Found selected language:', selectedLanguage);
      
      // Update form control - set the label value
      this.workflowForm.get('language').setValue(selectedLanguage.label);
      
      // Force change detection to update the UI
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('Current language value after change:', this.selectedValueLanguage);
      }, 0);

      // Track selection
      this.tracking.trackEvent(
        'action', `Changed Language to: ${selectedLanguage.label}`
      );
    } else {
      console.warn('Language not found for value:', languageValue);
    }
  }

  // File input element reference
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

  // Method to handle file selection
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Check if we're at the maximum number of documents
      if (this.uploadedFiles.length >= this.maxDocuments) {
        // Show message to user (you may want to use a toast or alert here)
        console.warn(`Maximum number of documents (${this.maxDocuments}) reached.`);
        return;
      }

      // Get the selected file
      const file = files[0];
      
      // Create a file object
      const newFile = {
        id: Date.now(), // Use timestamp as a unique ID
        name: file.name,
        size: this.formatFileSize(file.size),
        progress: 0,
        documentType: '',
        documentDescription:'',
        uploading: true,
        file: file // Store the actual file for upload
      };
      
      // Add the file to the array
      this.uploadedFiles.push(newFile);
      
      // Simulate upload progress
      this.simulateUploadProgress(newFile.id);
      
      // Track the action
      this.tracking.trackEvent('action', `Selected document: ${newFile.name}`);
      
      // Reset the file input
      this.fileInput.nativeElement.value = '';
      
      // Scroll to the new file after a brief delay to ensure the DOM is updated
      setTimeout(() => {
        const fileElements = document.querySelectorAll('.file-upload-card');
        if (fileElements.length > 0) {
          const lastFile = fileElements[fileElements.length - 1] as HTMLElement;
          lastFile.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }
  
  // Helper method to format file size
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // This method is now used for the "Add Document" button when files are already uploaded
  onAddDocument(): void {
    console.log('onAddDocument called');
    
    // Check if we're under the maximum document limit
    if (this.uploadedFiles.length >= this.maxDocuments) {
      console.warn(`Maximum number of documents (${this.maxDocuments}) reached.`);
      return;
    }
    
    // Try multiple ways to access the file input
    try {
      // First try using the ViewChild reference
      if (this.fileInput && this.fileInput.nativeElement) {
        console.log('Using ViewChild reference to click file input');
        this.fileInput.nativeElement.click();
        return;
      }
      
      // Try the global file input at the top level
      const globalFileInput = document.getElementById('global-document-file-input') as HTMLInputElement;
      if (globalFileInput) {
        console.log('Using global file input');
        globalFileInput.click();
        return;
      }
      
      // If ViewChild fails, try using getElementById
      const fileInputById = document.getElementById('document-file-input') as HTMLInputElement;
      if (fileInputById) {
        console.log('Using getElementById to click file input');
        fileInputById.click();
        return;
      }
      
      // If getElementById fails, try using querySelector
      const fileInputElement = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInputElement) {
        console.log('Using querySelector to click file input');
        fileInputElement.click();
        return;
      }
      
      // As a last resort, create a new file input dynamically and click it
      console.log('Creating dynamic file input as last resort');
      this.createFallbackFileInput();
      const newInput = document.getElementById('document-file-input') as HTMLInputElement;
      if (newInput) {
        newInput.click();
      } else {
        console.error('Failed to create and find dynamic file input');
      }
    } catch (err) {
      console.error('Error clicking file input:', err);
    }
  }

  // Helper method to simulate upload progress
  private simulateUploadProgress(fileId: number): void {
    // Find the file
    const file = this.uploadedFiles.find(f => f.id === fileId);
    if (!file) return;
    
    // Start at 0%
    file.progress = 0;
    
    // Simulate progress updates
    const interval = setInterval(() => {
      // Increment progress by a random amount between 5-15%
      const increment = Math.floor(Math.random() * 10) + 5;
      file.progress += increment;
      
      // Cap at 100%
      if (file.progress >= 100) {
        file.progress = 100;
        file.uploading = false;
        clearInterval(interval);
        
        // Set a default document type
        if (!file.documentType) {
          // Find a document type that matches the file name
          const matchingType = this.documentTypeOptions.find(option => 
            file.name.toLowerCase().includes(option.label.toLowerCase()));
          
          if (matchingType) {
            file.documentType = matchingType.value;
          } else {
            // Default to the first option
            file.documentType = this.documentTypeOptions[0].value;
          }
        }
        
        // Detect changes to update the UI
        this.cdr.detectChanges();
      }
    }, 500); // Update every 500ms
  }

  removeUploadedFile(fileId: number): void {
    this.uploadedFiles = this.uploadedFiles.filter(file => file.id !== fileId);
    this.tracking.trackEvent('action', `Removed uploaded file with ID: ${fileId}`);    
  }

  onDocumentTypeChange(fileId: number, documentType: string): void {
    const file = this.uploadedFiles.find(f => f.id === fileId);
    if (file) {
      file.documentType = documentType;
      this.tracking.trackEvent('action', `Set document type to ${documentType} for file ${file.name}`);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  ngAfterViewInit(): void {
    // Check if fileInput is properly initialized
    setTimeout(() => {
      if (!this.fileInput) {
        console.warn('fileInput reference not available after view init');
        // Create a fallback file input if the ViewChild reference isn't available
        this.createFallbackFileInput();
      } else {
        console.log('fileInput reference successfully initialized');
      }
    }, 0);
  }
  
  // Create a fallback file input if the ViewChild reference isn't working
  private createFallbackFileInput(): void {
    const existingInput = document.getElementById('document-file-input') as HTMLInputElement;
    if (!existingInput) {
      console.log('Creating fallback file input element');
      const input = document.createElement('input');
      input.type = 'file';
      input.id = 'document-file-input';
      input.accept = '.doc,.docx,.pdf,.txt';
      input.style.display = 'none';
      input.addEventListener('change', (event) => this.onFileSelected(event));
      document.body.appendChild(input);
    }
  }


  //
  multiUrlValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const urls = control.value
      .split(/[\n,]+/)
      .map(u => u.trim())
      .filter(Boolean);
    if (urls.length > 500) {
      return { maxUrlsExceeded: true };
    }  
    const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,63}(\/\S*)?$/;
    const allValid = urls.every(url => urlPattern.test(url));
    if (!allValid) return { invalidUrl: true };
    try {
      const firstDomain = new URL(urls[0]).hostname.toLowerCase();
      const allSameDomain = urls.every(url => {
        return new URL(url).hostname.toLowerCase() === firstDomain;
      });
      if (!allSameDomain) {
        return { multipleDomainsNotAllowed: true };
      }
    } catch {
      return { invalidUrl: true }; // In case URL parsing fails
    }
    return null;
  }
  individualUrlsChange() {
    this.workflowForm.controls['websiteUrl'].setValue('');
    const control = this.workflowForm.get('individualUrls');
    control?.updateValueAndValidity();
    this.isIndividualUrlVisible = true;
    this.isParentUrlVisible = false;
  }
  parentUrlChange() {
    this.workflowForm.controls['individualUrls'].setValue('');
    this.isIndividualUrlVisible = false;
    this.isParentUrlVisible = true;
  }

  //Upload
  contentAnalysisStatus = '';
  fileList = [];
  @ViewChild('ErrorAlertTemplate') ErrorAlertTemplate!: TemplateRef<any>;

  isFileAnalysis: boolean = true;
  handleChange({ file, fileList }: MwcUploadChangeParam): void {
    console.log('in')
    const status = file.status;
    if (status !== 'uploading') {
      console.log(file, fileList);
    }
    if (status === 'done') {
      this.fileList = [{
        uid: '-xxx',
        percent: 100,
        name: file.name,
        status: 'done',
      }];
      //this.msg.success(`${file.name} file uploaded successfully.`);
    } else if (status === 'error') {
      this.fileList = [{
        uid: '-xxx',
        percent: 100,
        name: file.name,
        status: 'error',
      }];
      //this.msg.error(`${file.name} file upload failed.`);
    }
  }
  beforeUpload = (file: MwcUploadFile): boolean => {
    file.status = 'uploading';
    file.percent = 50;
    this.fileList = [];
    this.fileList.push(file);
    this.fileList = [...this.fileList];
    let uploadedFile: any = file;
    const reader = new FileReader();

    reader.onload = () => {
      // This is where the content of the file will be available.
      this.uploadFile(file);
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
    };

    // Read the file as text
    reader.readAsText(uploadedFile);
    return false;
  }
  formDataForFile = new FormData();

  hasValidExtension(fileName: string, allowedExtensions: string[]): boolean {
    const regex = new RegExp(`\\.(${allowedExtensions.join('|')})$`, 'i');
    return regex.test(fileName);
  }


  uploadFile(file: any) {
    let fr = new FileReader();    

    if (this.fileList && this.fileList[0] && this.fileList.length > 0) {

      const allowedExtensions = ['xlsx', 'xls'];
      if (!this.hasValidExtension(this.fileList[0].name, allowedExtensions)) {
        this.fileList = [];        
        this.notification.snackbarError('Invalid supported file, Only xlsx and xls file allowed.');
        this.cdRef.detectChanges();
        return;
      }
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      var fileSize = this.fileList[0].size;
      if (fileSize > MAX_FILE_SIZE) {
        this.fileList = [];
       /*  this.errorMessage = "File size exceeds the 10MB limit. Please upload a smaller file.";
        this.notification.template(this.ErrorAlertTemplate, { mwcDuration: 3000, mwcStyle: { padding: '16px' }, mwcPlacement: 'top', mwcClass: 'error-notification-content' }); */
        this.notification.snackbarError('File size exceeds the 10MB limit. Please upload a smaller file.');
        this.cdRef.detectChanges();
        return;
      }      
      this.formDataForFile.set('file', this.fileList[0], this.fileList[0].name);
    }
    const totalDuration = 2000; // 2 seconds
    const steps = 20; // Number of updates (can be increased for smoother animation)
    const stepTime = totalDuration / steps; // Time for each step (milliseconds)
    this.contentAnalysisStatus = 'analyzing';
    interval(stepTime)
      .pipe(take(steps + 1)) // Run the interval 20 times (0% to 100%)
      .subscribe((step) => {
        const percentDone = Math.min((step / steps) * 100, 100); // Calculate progress
        if (percentDone == 100) {
          this.contentAnalysisStatus = '';
        }
        if (this.fileList.length > 0) {
          this.fileList[0].percent = Math.round(percentDone);
          this.fileList[0].status = percentDone === 100 ? 'done' : 'uploading';
          this.fileList = [...this.fileList];          
          this.cdRef.detectChanges();
        }
      });
    this.UploadKeywords();
    
  }

  UploadKeywords() {
    this.campaignService.uploadKeywordDocument(this.fileList[0]).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.selectedKeywords = (response.data.keywords || []).reduce((acc: string[], row) => acc.concat(row.keywords || []), []);
          this.cdRef.detectChanges();
        } else if (!response.success && response.message) {
          this.fileList = [];
          this.cdRef.detectChanges();
          this.notification.snackbarError(response.message);
        }
      },
      error: (err) => {
        console.error('keyword upload failed:', err);
        this.fileList = [];
        this.cdRef.detectChanges();
      },
    });
  }

  UploadInstructionsDocs() {
    let request = new UploadInstDocsRequest();
    //request.campaignId = this.campaignId;
    //request.profileId = this.profileId;
    request.files = this.uploadedFiles; // Extract the actual File objects
    this.campaignService.uploadInstructionDocuments(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response.success) {


          this.cdRef.detectChanges();
          console.log('Instruction Document uploaded successfully:', response);
        }
      },
      error: (err) => {
        console.error('Instruction Document upload failed:', err);
      },
      complete: () => {
        console.log('Instruction Document upload completed');
      },
    });
  }

  fetchBrandVoices(isReload: boolean = false) {
    if (isReload) { this.filteredBrandVoiceList = []; this.brandVoiceList = []; }
    var brandVoiceListRequest = new GetBrandVoiceListRequest();
    brandVoiceListRequest.profileId = +this._storageHelper.user.pId;

    this.isLoading = true;
    this.articleService.GetBrandVoiceList(brandVoiceListRequest).subscribe((response: GetBrandVoiceListResponse) => {
      console.log(response);
      if (response.errorCode != 0 && !response.errorMessage) {
        //show error messages
      } else {
        if (response.totalRecords > 0) this.isNoCreateBrandVoice = false;
        response.brandVoices.forEach((brandVoice: BrandVoice) => {
          if (brandVoice.isDefault) {
            this.isDefaultBVAvailable = true;
            this.defaultBVId = brandVoice.brandVoiceId;
          }
          this.brandVoiceList.push({
            label: brandVoice.title, value: brandVoice.brandVoiceId.toString(), tone: brandVoice.thingBrandVoice.thingJson.voiceTone, createdOn: brandVoice.createdDate.toString(), createdBy: brandVoice.authorName, brandVoiceDocument: brandVoice.thingBrandVoice.thingJson.brandVoiceDocument, details: brandVoice.thingBrandVoice.thingJson, isDefault: brandVoice.isDefault, isSelectedBV: false
          })
        });
        this.filteredBrandVoiceList = this.brandVoiceList;
        let data;
        if (this.isDefaultBVAvailable && this.defaultBVId != 0) {
          data = this.brandVoiceList.filter(item => { if (item.value == this.defaultBVId) return item })[0];
          this.filteredBrandVoiceList = this.filteredBrandVoiceList.map(item => ({
            ...item,
            isDefault: false,
            isSelectedBV: item.value == this.defaultBVId
          }));

          if (data != null && data != undefined) {
            this.brandVoiceDetails = data;
            this.workflowForm.patchValue(
              {
                brandVoiceDocument: data.brandVoiceDocument,
                brandVoiceDetails: data.details,
                brandVoiceId: +data.value
              }
            );
            this.selectedBrandVoice = data.value;
          }
        }
        //if (this.campaignData != null && this.campaignData.brandVoiceId != 0) {
        //  let data = this.brandVoiceList.filter(item => { if (item.value == this.campaignData.brandVoiceId.toString()) return item })[0];
        //  if (data != null && data != undefined) {
        //    this.isDefaultBVAvailable = false;
        //    this.defaultBVId = 0;
        //    this.brandVoiceDetails = data;
        //    this.generateAiForm.patchValue(
        //      {
        //        brandVoiceDocument: data.brandVoiceDocument,
        //        brandVoiceDetails: data.details,
        //        brandVoiceId: +data.value
        //      }
        //    );
        //    this.selectedBrandVoice = data.value;
        //    this.filteredBrandVoiceList = this.filteredBrandVoiceList.map(item => ({
        //      ...item,
        //      isDefault: false,
        //      isSelectedBV: item.value === this.campaignData.brandVoiceId.toString()
        //    }));
        //  }
        //}
      }
      this.isLoading = false;
    }, (error: any) => {
      this.isLoading = false;
    });
  }

  fetchTargetAudiences(isReload: boolean = false) {
    if (isReload) this.targetAudienceList = []; this.filteredTargetAudienceList = [];
    var targetAudienceListRequest = new GetTargetAudienceListRequest();
    targetAudienceListRequest.profileId = +this._storageHelper.user.pId;

    this.isLoading = true;
    this.articleService.GetTargetAudienceList(targetAudienceListRequest).subscribe((response: GetTargetAudienceListResponse) => {
      console.log(response);
      if (response.errorCode != 0 && !response.errorMessage) {
        //show error messages
      } else {
        if (response.totalRecords > 0) this.isNoTargetAudience = false;
        //this.isNoTargetAudience = true;
        response.targteAudiences.forEach((targetAudience: TargetAudience) => {
          if (targetAudience.isDefault) {
            this.isDefaultTAAvailable = true;
            this.defaultTAId = targetAudience.targetAudienceId;
          }
          this.targetAudienceList.push({
            "targetAudienceId": targetAudience.targetAudienceId,
            "title": targetAudience.title,
            "authorName": targetAudience.authorName,
            "thingTargetAudience": {
              "thingJson": {
                "title": targetAudience.title,
                "gender": targetAudience.thingTargetAudience.thingJson.gender,
                "ageGroup": targetAudience.thingTargetAudience.thingJson.ageGroup,
                "relationshipStatus": targetAudience.thingTargetAudience.thingJson.relationshipStatus,
                "industry": targetAudience.thingTargetAudience.thingJson.industry,
                "interests": targetAudience.thingTargetAudience.thingJson.interests,
                "intent": targetAudience.thingTargetAudience.thingJson.intent
              }
            },
            "createdDate": targetAudience.createdDate,
            "isDefault": targetAudience.isDefault,
            "isSelectedTA": false
          });
        });
        this.filteredTargetAudienceList = this.targetAudienceList;
        //if (this.isRegenerateFlow) {
        //  this.defaultTAId = this.mwcModalData.AIData.topicIdea.taId;
        //  this.filteredTargetAudienceList = this.filteredTargetAudienceList.map(item => ({
        //    ...item,
        //    isDefault: false,
        //    isSelectedTA: item.targetAudienceId === this.mwcModalData.AIData.topicIdea.taId
        //  }));
        //}
        if (this.isDefaultTAAvailable && this.defaultTAId != 0) {
          let data = this.targetAudienceList.filter(item => { if (item.targetAudienceId == this.defaultTAId) return item })[0];
          if (data != null && data != undefined) {
            this.targetAudienceDetails = data;
            this.workflowForm.patchValue(
              {
                targetAudienceDetails: data.thingTargetAudience.thingJson,
                targetAudienceId: +data.targetAudienceId
              }
            );
          }
          this.selectedTargetAudience = data.title;
        }
        //if (this.campaignData != null && this.campaignData.targetAudienceId != 0) {
        //  let data = this.targetAudienceList.filter(item => { if (item.targetAudienceId == this.campaignData.targetAudienceId) return item })[0];
        //  if (data != null && data != undefined) {
        //    this.isDefaultTAAvailable = false;
        //    this.defaultTAId = 0;
        //    this.targetAudienceDetails = data;
        //    this.generateAiForm.patchValue(
        //      {
        //        targetAudienceDetails: data.thingTargetAudience.thingJson,
        //        targetAudienceId: +data.targetAudienceId
        //      }
        //    );
        //  }
        //  this.selectedTargetAudience = data.title;
        //  this.filteredTargetAudienceList = this.filteredTargetAudienceList.map(item => ({
        //    ...item,
        //    isDefault: false,
        //    isSelectedTA: item.targetAudienceId === this.campaignData.targetAudienceId
        //  }));
        //}
      }
      this.isLoading = false;
    }, (error: any) => {
      this.isLoading = false;
    });
  }

  openRedirectToMPC(event) {
    if (event == 'targetAudience') {
      const route = this.router.createUrlTree(['/target-audience']);
      const url = this.router.serializeUrl(route);
      window.open(url, '_blank');
    } else {
      const route = this.router.createUrlTree(['/brand-voice']);
      const url = this.router.serializeUrl(route);
      window.open(url, '_blank');
    }
    //this.newTemplateHelperService.closeTemplateSelector();
    //this.modal.destroy();
  }

  getDaysDifference(inputDate) {
    // Convert input date to a Date object
    const date = new Date(inputDate);
    // Get today's date
    const today = new Date();

    // Reset time components to compare only dates
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    // Calculate difference in milliseconds
    const timeDifference = Math.abs(today.getTime() - date.getTime());

    // Convert milliseconds to days
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    // Format the result
    if (daysDifference === 0) {
      return "Today";
    } else if (daysDifference === 1) {
      return "1 day ago";
    } else {
      return `${daysDifference} days ago`;
    }
  }

}
